// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import * as GateTravelMgr from '../../utils/game/GateTravelManager';
import STORAGE_KEYS from '../../utils/system/STORAGE_KEYS';

/**
 * Custom hook for warp gate activation and universe switching
 * Extracted from NavigationPanel to reduce file size
 */
export const useWarpGateTravel = ({
  antimatterFuel,
  maxFuel,
  currentUniverse,
  discoveredSystems,
  travelThroughGate,
  validateOnEnter,
  enrichLocationWithCoordinates,
  generatePlanetStars,
  setCurrentLocation,
  setSelectedSystem,
  setDiscoveredSystems,
  setShowOrbitScreen,
  setShowGateWarpEffect,
  setGateDestinationUniverseId,
  setExpandedMap,
  setHasArrived,
  setPlanetStars,
  setActiveEvent
}) => {
  const handleGateActivation = (gate) => {
    const fuelCheck = GateTravelMgr.checkGateActivation(gate, antimatterFuel, maxFuel);
    if (!fuelCheck.success) {
      setActiveEvent({
        ...fuelCheck.warning,
        actions: [{ label: 'UNDERSTOOD', effect: () => setActiveEvent(null) }]
      });
      return;
    }

    const travelResult = GateTravelMgr.prepareGateTravel(gate, travelThroughGate);
    if (!travelResult.success) {
      console.error('[useWarpGateTravel] Gate travel preparation failed:', travelResult.error);
      return;
    }

    setShowOrbitScreen(false);
    setExpandedMap(false);
    setShowGateWarpEffect(true);

    const { destinationGate, destinationUniverse } = travelResult;
    setGateDestinationUniverseId(destinationUniverse?.id || null);
    const gateTravelTime = GateTravelMgr.getGateTravelTime();

    setTimeout(() => {
      setShowGateWarpEffect(false);

      if (GateTravelMgr.shouldDiscoverGate(destinationGate, discoveredSystems)) {
        setDiscoveredSystems(prev => new Set([...prev, destinationGate.id]));
      }

      setCurrentLocation(enrichLocationWithCoordinates(destinationGate));
      setSelectedSystem(destinationGate);

      const saveData = GateTravelMgr.createGateArrivalSaveData(destinationGate, destinationUniverse);
      localStorage.setItem(STORAGE_KEYS.CURRENT_LOCATION, JSON.stringify(saveData));

      // Validate destination universe (only if we have a valid universe object)
      if (destinationUniverse && destinationUniverse.id) {
        const validation = validateOnEnter(destinationUniverse, {
          [currentUniverse.id]: currentUniverse,
          [destinationUniverse.id]: destinationUniverse
        });
        if (!validation.valid) {
          console.warn(`[UniverseValidation] Issues detected in ${destinationUniverse.id}:`, validation.issues.critical);
        }
      } else {
        console.error('[useWarpGateTravel] Invalid destination universe object:', destinationUniverse);
      }

      setHasArrived(true);
      setShowOrbitScreen(true);
      setPlanetStars(generatePlanetStars());
    }, gateTravelTime);
  };

  return { handleGateActivation };
};
